import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Container,
  Stack,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
  Checkbox,
} from "@mui/material";
import { isEmpty, map } from "lodash";
import theme, { DcInput } from "../../assets/theme/theme";
import { useCallback, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { analytics, rtdb } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { push, ref, serverTimestamp } from "firebase/database";
import { useUserContext } from "../../contexts/UserContext";
import { logEvent } from "firebase/analytics";
import React from "react";
import constants from "../../constants";
import { postData } from "../../utils/fetchUtils";
import { usePaymentContext } from "../../contexts/PaymentContext";

const QUERY_TYPES = ["Immigration", "Taxes (CPA)", "Others"];

export function GetExpertHelp() {
  const { currentUser } = useAuth();
  const { user } = useUserContext();
  const { pricingPlans } = usePaymentContext();
  const currentTier = user?.tier || "FREE";

  const [queryType, setQueryType] = useState<string | null>(null);
  const [queryDesc, setQueryDesc] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState<string | null>(user?.email as string);
  const [radioValue, setRadioValue] = useState<string | null>(null);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [contactedRadioValue, setContactedRadioValue] = useState<string>("email");
  const [termsChecked, setTermsChecked] = useState(false);


  const payForQuery = React.useCallback(
    async (data: {
      uid: string | undefined;
      queryId: string;
      priceLookupKey: string;
    }) => {
      try {
        logEvent(analytics, "begin_checkout", { plan: "IMMIGRATION_QUERY_PLAN" });
        const url = `${constants.middleHost}/stripe/create-expert-help-checkout-session`;
        const token = await currentUser?.getIdToken();
        const headers = {
          Authorization: `Bearer ${token}`,
        };
        const res = await postData(url, data, headers);
        window.open(res.data.url, "_blank");
      } catch (error: any) {
        console.error(error);
        logEvent(analytics, "error_checkout", {
          plan: "IMMIGRATION_QUERY_PLAN",
          status: "error",
          message: error.message,
        });
      }
    },
    [currentUser]
  );

  const clearForm = useCallback(() => {
    setQueryType("");
    setQueryDesc("");
    setContactInfo("");
    setRadioValue("");
    setIsSubmittingForm(false);
    setFormError("");
  }, []);

  const handleQueryTypeChange = useCallback(
    (evt) => {
      setQueryType(evt.target.value);
    },
    [setQueryType]
  );

  const handleQueryDescChange = useCallback(
    (evt) => {
      setQueryDesc(evt.target.value);
    },
    [setQueryDesc]
  );

  const handleContactInfo = useCallback(
    (evt) => {
      setContactInfo(evt.target.value);
    },
    [setContactInfo]
  );

  const handleRadioChange = useCallback((evt) => {
    setRadioValue(evt.target.value);
  }, []);

  const validateForm = useCallback(() => {
    const result =
      !isEmpty(queryType) ||
      !isEmpty(queryDesc) ||
      !isEmpty(contactInfo) ||
      !isEmpty(radioValue);

    if (!result) setFormError("Required fields missing!");
    return result;
  }, [contactInfo, queryDesc, queryType, radioValue]);

  const handleSubmit = useCallback(
    async (evt) => {
      try {
        evt.preventDefault();
        if (!validateForm()) return;
        setIsSubmittingForm(true);
        const refStr = `immigration_queries/${currentUser?.uid}`;
        const immigrationQueriesRef = ref(rtdb, refStr);

        const data = await push(immigrationQueriesRef, {
          formData: {
            queryType,
            queryDesc,
            contactInfo,
            radioValue,
          },
          user: {
            email: currentUser?.email,
            firstName: user?.firstName,
            lastName: user?.lastName,
            displayName: currentUser?.displayName,
          },
          payment_status: "pending",
          created_at: serverTimestamp(),
        });
        await payForQuery({
          uid: currentUser?.uid,
          queryId: data?.key as string,
          priceLookupKey: `IMMIGRATION_QUERY_PRICE_LOOKUP_KEY_${currentTier}`,
        });
        setIsSubmittingForm(false);
        clearForm();
      } catch (err) {
        console.error(err);
        setIsSubmittingForm(false);
        setFormError("Failed to submit form, please retry.");
      }
    },
    [clearForm, contactInfo, currentTier, currentUser?.displayName, currentUser?.email, currentUser?.uid, payForQuery, queryDesc, queryType, radioValue, user?.firstName, user?.lastName, validateForm]
  );

  return (
    <Box bgcolor={"white"} sx={{ minHeight: "calc(100%)" }} className="get-expert-model">
      <Container disableGutters sx={{ width: "fit-content", bgcolor: "white" }}>
        <Stack
          className="get-expert-form-wrapper"
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 6,
          }}
          spacing={4}

        >
          <Typography
            variant="h4"
            fontSize={24}
            fontWeight={600}
            color="primary"
          >
            Get Expert Help
            <Typography component="p" color={theme.palette.text.secondary}>
              Immigration & CPA support from our experts.
            </Typography>
          </Typography>

          <Stack
            direction="column"
            spacing={4}
            maxWidth={600}
            component="form"
            onSubmit={handleSubmit}
            sx={{
              "@media (min-width: 600px)": {
                minWidth: 600,
              },
            }}
          >
            <FormControl variant="standard" required className="get-expert-form">
              <InputLabel shrink htmlFor="query-type" className="get-export-form-label">
                What kind of support do you need?
              </InputLabel>
              <Select
                className="get-expert-form-input"
                name="query-type"
                onChange={handleQueryTypeChange}
                inputProps={{
                  name: "query-type",
                  id: "query-type",
                }}
                displayEmpty
                placeholder="Select from the list"
                value={queryType || ""}
                input={<DcInput />}
              >
                <MenuItem key="empty-label-doc-type" disabled value="">
                  <em>Select from the list</em>
                </MenuItem>

                {map(QUERY_TYPES, (f, i) => {
                  return (
                    <MenuItem value={f} key={`${f}-${i}`}>
                      {f}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl variant="standard" required className="get-expert-form">
              <InputLabel shrink htmlFor="query-desc" className="get-export-form-label">
                Describe your situation/problem in detail
              </InputLabel>
              <DcInput
                className="get-expert-form-input"
                name="query-desc"
                value={queryDesc}
                required={true}
                minRows={4}
                onChange={handleQueryDescChange}
                fullWidth
                placeholder="Your query description"
                multiline
              />
            </FormControl>

            <FormControl variant="standard" required>
              <FormLabel htmlFor="form-radio-btns">
                Urgency of your request
              </FormLabel>
              <RadioGroup
                row
                name="form-radio-btns"
                onChange={handleRadioChange}
                defaultValue={"i-can-wait"}
                value={radioValue}
              >
                <FormControlLabel
                  value="i-can-wait"
                  control={<Radio />}
                  label="I can wait"
                />
                <FormControlLabel
                  value="moderately-urgent"
                  control={<Radio />}
                  label="Moderately urgent"
                />
                <FormControlLabel
                  value="its-urgent"
                  control={<Radio />}
                  label="It's urgent"
                />
              </RadioGroup>
            </FormControl>

            <FormControl variant="standard" required className="get-expert-form">
              <InputLabel shrink htmlFor="contact-info" className="get-export-form-label">
                How would you like to be contacted? Please share
              </InputLabel>
              <RadioGroup
                row
                name="contacted-radio-btns"
                onChange={(evt) => {
                  setContactedRadioValue(evt.target.value);
                  if (evt.target.value === "email") {
                    setContactInfo(user?.email as string);
                  } else {
                    setContactInfo(user?.phoneNumber as string);
                  }
                }}
                defaultValue={"email"}
                value={contactedRadioValue}
                sx={{ mt: 3 }}
              >
                <FormControlLabel
                  value="email"
                  control={<Radio />}
                  label="Email"
                />
                <FormControlLabel
                  value="phone"
                  control={<Radio />}
                  label="Phone"
                />
              </RadioGroup>
              <DcInput
                className="get-expert-form-input"
                name="contact-info"
                value={contactInfo}
                required={true}
                onChange={handleContactInfo}
                fullWidth
                placeholder="Your email address"
                sx={{ mt: 1 }}
              />
            </FormControl>
            {formError && (
              <Alert variant="filled" severity="error">
                {formError}
              </Alert>
            )}
            <LoadingButton
              variant="contained"
              type="submit"
              loading={isSubmittingForm}
              disabled={!termsChecked}
              sx={{
                "&:disabled": {
                  backgroundColor: theme.palette.grey[300],
                  color: theme.palette.common.white,
                },
              }}
            >
              {currentTier === "FREE" && `Pay ${pricingPlans?.FREE.currency}${pricingPlans?.FREE.queryPrice} and Submit Query`}
              {currentTier === "BASIC" && `Pay ${pricingPlans?.BASIC_MONTHLY.currency}${pricingPlans?.BASIC_MONTHLY.queryPrice} and Submit Query`}
              {currentTier === "PREMIUM" && `Pay ${pricingPlans?.PREMIUM_MONTHLY.currency}${pricingPlans?.PREMIUM_MONTHLY.queryPrice} and Submit Query`}
            </LoadingButton>
            <Box
              sx={{
                display: "flex",
                alignItems: "start"
              }}
            >
              <Checkbox
                sx={{
                  color: theme.palette.primary.main,
                  "&.Mui-checked": {
                    color: theme.palette.primary.main,
                  },
                }}
                checked={termsChecked}
                onClick={() => setTermsChecked(!termsChecked)}
              />
              <Typography
                component="span"
                fontSize={14}
                color={theme.palette.text.secondary}
                ml={1}
                fontWeight={400}
              >
                This paid service provides expert immigration guidance (non-legal). Experts are not attorneys. Fee includes a detailed reply and an optional follow-up call.
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
